/**
 * Jira Configuration Dialog Component
 *
 * Modal dialog for managing the organization's Jira integration.
 * States: not configured (setup form), configured (view), editing, testing connection.
 * Follows the LogfireConfigDialog pattern.
 */

import { ReactNode, useState, useEffect } from "react";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  useJiraIntegration,
  useCreateJiraIntegration,
  useUpdateJiraIntegration,
  useDeleteJiraIntegration,
  useTestJiraConnection,
} from "@/hooks/useJiraIntegration";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import type { JiraIntegrationCreate } from "@/types/jiraIntegration";

interface JiraConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type DialogView = "view" | "setup" | "edit" | "confirm-delete";

export function JiraConfigDialog({ children, open, onOpenChange }: JiraConfigDialogProps) {
  const { data: jiraIntegration, isLoading } = useJiraIntegration();
  const { isOrganizationOwner } = useOrganizationContext();

  const createMutation = useCreateJiraIntegration();
  const updateMutation = useUpdateJiraIntegration();
  const deleteMutation = useDeleteJiraIntegration();
  const testMutation = useTestJiraConnection();

  const [view, setView] = useState<DialogView>("view");
  const [showToken, setShowToken] = useState(false);
  const [formData, setFormData] = useState<JiraIntegrationCreate>({
    jira_base_url: "",
    jira_email: "",
    api_token: "",
    default_project_key: "",
  });
  const [testPassed, setTestPassed] = useState(false);

  const isConfigured = jiraIntegration != null;

  // Set the correct initial view when the dialog opens or data changes
  useEffect(() => {
    if (open) {
      if (isConfigured) {
        setView("view");
      } else {
        setView("setup");
      }
      resetForm();
    }
  }, [open, isConfigured]);

  function resetForm() {
    setFormData({
      jira_base_url: "",
      jira_email: "",
      api_token: "",
      default_project_key: "",
    });
    setTestPassed(false);
    setShowToken(false);
    testMutation.reset();
    createMutation.reset();
    updateMutation.reset();
  }

  function handleStartEdit() {
    if (!jiraIntegration) return;
    setFormData({
      jira_base_url: jiraIntegration.jira_base_url,
      jira_email: jiraIntegration.jira_email,
      api_token: "", // user must re-enter token to change it
      default_project_key: jiraIntegration.default_project_key ?? "",
    });
    setTestPassed(false);
    testMutation.reset();
    setView("edit");
  }

  async function handleTestConnection() {
    testMutation.reset();
    setTestPassed(false);
    try {
      // In setup/edit mode, send inline credentials so the test works
      // before the integration is saved to the DB.
      const credentials =
        view === "setup" || view === "edit"
          ? {
              jira_base_url: formData.jira_base_url.trim(),
              jira_email: formData.jira_email.trim(),
              api_token: formData.api_token.trim(),
              default_project_key: formData.default_project_key?.trim() || undefined,
            }
          : undefined;

      const result = await testMutation.mutateAsync({
        projectKey: formData.default_project_key?.trim() || undefined,
        credentials,
      });
      if (result.success) {
        setTestPassed(true);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    if (!formData.jira_base_url.trim() || !formData.jira_email.trim()) {
      return;
    }

    try {
      if (view === "setup") {
        if (!formData.api_token.trim()) return;
        await createMutation.mutateAsync({
          jira_base_url: formData.jira_base_url.trim(),
          jira_email: formData.jira_email.trim(),
          api_token: formData.api_token.trim(),
          default_project_key: formData.default_project_key?.trim() || undefined,
        });
      } else {
        // Edit mode — only send fields that changed
        const updates: Record<string, string | boolean | undefined> = {};
        if (formData.jira_base_url.trim() !== jiraIntegration?.jira_base_url) {
          updates.jira_base_url = formData.jira_base_url.trim();
        }
        if (formData.jira_email.trim() !== jiraIntegration?.jira_email) {
          updates.jira_email = formData.jira_email.trim();
        }
        if (formData.api_token.trim()) {
          updates.api_token = formData.api_token.trim();
        }
        const newProjectKey = formData.default_project_key?.trim() || undefined;
        if (newProjectKey !== (jiraIntegration?.default_project_key ?? undefined)) {
          updates.default_project_key = newProjectKey;
        }
        await updateMutation.mutateAsync(updates);
      }
      setView("view");
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync();
      setView("setup");
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  }

  function handleCancel() {
    if (isConfigured) {
      setView("view");
    }
    resetForm();
    testMutation.reset();
    createMutation.reset();
    updateMutation.reset();
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;
  const isFormValid =
    formData.jira_base_url.trim() !== "" &&
    formData.jira_email.trim() !== "" &&
    (view === "edit" || formData.api_token.trim() !== "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Jira Integration</DialogTitle>
          <DialogDescription>
            Connect your Jira Cloud instance to create and track security issue tickets
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : view === "confirm-delete" ? (
          /* Delete Confirmation */
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Delete Jira Integration?</p>
                <p className="text-xs mt-1">
                  This will remove the Jira connection for your organization.
                  Existing linked tickets will remain but new tickets cannot be created
                  and status syncing will stop.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setView("view")}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Integration"
                )}
              </Button>
            </div>
          </div>
        ) : view === "view" && isConfigured ? (
          /* Configured View */
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {jiraIntegration.is_active ? (
                <Badge className="bg-green-600 hover:bg-green-700 text-xs">
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>

            {/* GEC-28: Error warnings */}
            {jiraIntegration.last_error && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-xs">Last error: {jiraIntegration.last_error}</p>
                  {jiraIntegration.consecutive_errors > 3 && (
                    <p className="text-xs mt-1">
                      Integration may need attention ({jiraIntegration.consecutive_errors} consecutive errors).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Integration Details */}
            <div className="space-y-3">
              <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                <span className="text-muted-foreground">Base URL</span>
                <span className="font-medium truncate">{jiraIntegration.jira_base_url}</span>

                <span className="text-muted-foreground">Email</span>
                <span className="font-medium truncate">{jiraIntegration.jira_email}</span>

                <span className="text-muted-foreground">API Token</span>
                <span className="font-medium font-mono">
                  {jiraIntegration.token_last_four
                    ? `\u2022\u2022\u2022\u2022${jiraIntegration.token_last_four}`
                    : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                </span>

                <span className="text-muted-foreground">Project Key</span>
                <span className="font-medium">
                  {jiraIntegration.default_project_key || "Not set"}
                </span>

                {jiraIntegration.last_used_at && (
                  <>
                    <span className="text-muted-foreground">Last Used</span>
                    <span className="font-medium">
                      {new Date(jiraIntegration.last_used_at).toLocaleString()}
                    </span>
                  </>
                )}
              </div>
            </div>

            <Separator />

            {/* Actions */}
            {isOrganizationOwner && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleStartEdit} className="flex-1">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setView("confirm-delete")}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
            {!isOrganizationOwner && (
              <p className="text-xs text-muted-foreground">
                Only the organization owner can edit or delete this integration.
              </p>
            )}
          </div>
        ) : (
          /* Setup / Edit Form */
          <form onSubmit={handleSave} className="space-y-4">
            <h3 className="text-sm font-medium">
              {view === "edit" ? "Edit Integration" : "Set Up Jira Integration"}
            </h3>

            {/* Test Result: Success */}
            {testMutation.isSuccess && testMutation.data?.success && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection successful!</p>
                  <p className="text-xs mt-1">{testMutation.data.message}</p>
                </div>
              </div>
            )}

            {/* Test Result: Failed */}
            {testMutation.isSuccess && !testMutation.data?.success && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection test failed</p>
                  <p className="text-xs mt-1">{testMutation.data?.message}</p>
                </div>
              </div>
            )}

            {/* Test Result: Error */}
            {testMutation.isError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection test failed</p>
                  <p className="text-xs mt-1">
                    {testMutation.error instanceof Error
                      ? testMutation.error.message
                      : "Unable to test connection"}
                  </p>
                </div>
              </div>
            )}

            {/* Save Error */}
            {saveError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Failed to save</p>
                  <p className="text-xs mt-1">
                    {saveError instanceof Error ? saveError.message : "Unable to save integration"}
                  </p>
                </div>
              </div>
            )}

            {/* Jira Base URL */}
            <div className="space-y-2">
              <Label htmlFor="jira-base-url">
                Jira Base URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="jira-base-url"
                type="url"
                placeholder="https://yourcompany.atlassian.net"
                value={formData.jira_base_url}
                onChange={(e) =>
                  setFormData({ ...formData, jira_base_url: e.target.value })
                }
                autoComplete="off"
                required
                disabled={isSaving}
              />
            </div>

            {/* Jira Email */}
            <div className="space-y-2">
              <Label htmlFor="jira-email">
                Jira Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="jira-email"
                type="email"
                placeholder="your-email@company.com"
                value={formData.jira_email}
                onChange={(e) =>
                  setFormData({ ...formData, jira_email: e.target.value })
                }
                autoComplete="off"
                required
                disabled={isSaving}
              />
            </div>

            {/* API Token */}
            <div className="space-y-2">
              <Label htmlFor="jira-api-token">
                API Token{" "}
                {view === "setup" && <span className="text-destructive">*</span>}
                {view === "edit" && (
                  <span className="text-xs text-muted-foreground font-normal">
                    (leave blank to keep current token)
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="jira-api-token"
                  type={showToken ? "text" : "password"}
                  placeholder={
                    view === "edit"
                      ? "Enter new token to update"
                      : "Enter your Jira API token"
                  }
                  value={formData.api_token}
                  onChange={(e) =>
                    setFormData({ ...formData, api_token: e.target.value })
                  }
                  autoComplete="off"
                  required={view === "setup"}
                  disabled={isSaving}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowToken(!showToken)}
                  tabIndex={-1}
                >
                  {showToken ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Generate an API token at{" "}
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Atlassian Account Settings
                </a>
              </p>
            </div>

            {/* Default Project Key */}
            <div className="space-y-2">
              <Label htmlFor="jira-project-key">Default Project Key (optional)</Label>
              <Input
                id="jira-project-key"
                placeholder="e.g. SEC"
                value={formData.default_project_key ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_project_key: e.target.value.toUpperCase(),
                  })
                }
                autoComplete="off"
                disabled={isSaving}
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">
                Pre-fills the project key when creating tickets from security issues.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              {(view === "edit" || isConfigured) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={!isFormValid || testMutation.isPending || isSaving}
              >
                {testMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  "Test Connection"
                )}
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : testPassed ? (
                  "Save"
                ) : (
                  "Save"
                )}
              </Button>
            </div>

            {/* Warning if saving without testing */}
            {!testPassed && isFormValid && !testMutation.isPending && (
              <p className="text-xs text-muted-foreground">
                Tip: Test the connection before saving to verify your credentials.
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
